// Live surf-forecast fetching for the Forecast page. Data comes straight from
// Open-Meteo in the browser — it's free, keyless and CORS-enabled, so no API
// key or backend worker is required:
//   - Swell / waves : marine-api.open-meteo.com  (wave + swell trains, periods)
//   - Wind          : api.open-meteo.com/v1/forecast (10 m speed/dir/gusts)
//   - Tide          : the marine API's free sea_level_height_msl curve
//
// Everything is normalized into the same SI hourly shape used by tripConditions
// ({ t: epochSec, swellH(m), swellDir(deg), windSpd(km/h), tideM(m), ... }) so we
// can reuse all of utils/conditions.js (groupByDay, summarizeDay, sampleAt, …).
import { mToFt, kmhToKn } from './conditions';

const HOUR = 3600;
const r1 = (v) => (Number.isFinite(v) ? Math.round(v * 10) / 10 : null);
const r0 = (v) => (Number.isFinite(v) ? Math.round(v) : null);
// Models report a spectral MEAN swell period (Tm); surf forecasts quote PEAK
// period (Tp ≈ 1.3·Tm for swell). Convert so values match Surfline/buoys.
const SWELL_PEAK_FACTOR = 1.3;
const peakFromMean = (tm) => (Number.isFinite(tm) ? Math.round(tm * SWELL_PEAK_FACTOR * 10) / 10 : null);

// Move a point `km` km along a compass bearing (great-circle) — used to nudge a
// wave-model sample offshore when a break's exact coordinate lands on a dead
// coastal cell of the coarse 0.25° wave grid.
function offsetPoint(lat, lng, bearingDeg, km) {
  const R = 6371;
  const d = km / R;
  const b = (bearingDeg * Math.PI) / 180;
  const la = (lat * Math.PI) / 180;
  const lo = (lng * Math.PI) / 180;
  const la2 = Math.asin(Math.sin(la) * Math.cos(d) + Math.cos(la) * Math.sin(d) * Math.cos(b));
  const lo2 = lo + Math.atan2(Math.sin(b) * Math.sin(d) * Math.cos(la), Math.cos(d) - Math.sin(la) * Math.sin(la2));
  return [(la2 * 180) / Math.PI, (lo2 * 180) / Math.PI];
}
// True when a wave series is effectively all-zero (blocked coastal cell).
const isDeadWaveSeries = (h) => {
  const a = h && h.swell_wave_height;
  if (!Array.isArray(a) || !a.length) return true;
  const live = (arr) => Array.isArray(arr) && arr.some((v) => Number.isFinite(v) && v > 0.05);
  return !live(a) && !live(h.wave_height);
};

const MARINE_VARS = [
  'wave_height', 'wave_direction', 'wave_period',
  'swell_wave_height', 'swell_wave_direction', 'swell_wave_period',
  'secondary_swell_wave_height', 'secondary_swell_wave_direction', 'secondary_swell_wave_period',
  'tertiary_swell_wave_height', 'tertiary_swell_wave_direction', 'tertiary_swell_wave_period',
  'wind_wave_height',
  'sea_level_height_msl',
].join(',');
// NCEP GFS Wave exposes up to three swell partitions + combined sea, but not the
// nearshore extras (sea level) — request only what it has.
const MARINE_LONG_VARS = [
  'wave_height', 'wave_direction', 'wave_period',
  'swell_wave_height', 'swell_wave_direction', 'swell_wave_period',
  'secondary_swell_wave_height', 'secondary_swell_wave_direction', 'secondary_swell_wave_period',
  'tertiary_swell_wave_height', 'tertiary_swell_wave_direction', 'tertiary_swell_wave_period',
].join(',');
const WIND_VARS = 'wind_speed_10m,wind_direction_10m,wind_gusts_10m';

const sleep = (ms) => new Promise((res) => setTimeout(res, ms));

// Open-Meteo rate-limits bursts (9 spots × 3 calls on page load). Retry 429s
// with jittered backoff so no spot ends up with a blank forecast.
async function getJson(url, tries = 4) {
  for (let i = 0; i < tries; i += 1) {
    const signal = AbortSignal.timeout ? AbortSignal.timeout(20000) : undefined;
    const r = await fetch(url, { signal });
    if (r.status === 429) {
      const ra = Number(r.headers.get('retry-after')) || 0;
      await sleep(ra > 0 ? ra * 1000 : 1200 * (i + 1) + Math.random() * 600);
      continue;
    }
    if (!r.ok) throw new Error(`${r.status} ${url.split('?')[0].replace(/^https?:\/\//, '')}`);
    return r.json();
  }
  throw new Error('429 rate-limited');
}

// Find tide highs/lows from the hourly sea-level series (parabolic sub-hour fit).
function deriveTideExtremes(hours) {
  const pts = hours.filter((h) => Number.isFinite(h.tideM));
  const ex = [];
  for (let i = 1; i < pts.length - 1; i += 1) {
    const a = pts[i - 1].tideM;
    const b = pts[i].tideM;
    const c = pts[i + 1].tideM;
    const isHigh = b >= a && b > c;
    const isLow = b <= a && b < c;
    if (!isHigh && !isLow) continue;
    const denom = a - 2 * b + c;
    const off = Math.abs(denom) > 1e-6 ? Math.max(-0.5, Math.min(0.5, (0.5 * (a - c)) / denom)) : 0;
    ex.push({ t: Math.round(pts[i].t + off * HOUR), heightM: r1(b - 0.25 * (a - c) * off), type: isHigh ? 'high' : 'low' });
  }
  return ex;
}

// Annotate each hour with a normalized tide stage 0..1 (0 = local low, 1 = local
// high) from a rolling ~one-cycle window, so it's comparable across spots and
// spring/neap ranges and lets per-spot tide preferences (low/mid/high) gate
// windows. Idempotent and safe to run on backend-cached hours that only carry
// raw tideM (the cache builder doesn't compute the stage).
export function attachTideStage(hours) {
  if (!Array.isArray(hours)) return hours;
  const WIN = 13; // hours ≈ one semidiurnal cycle
  for (let i = 0; i < hours.length; i += 1) {
    const v = hours[i].tideM;
    if (v == null) { hours[i].tideStage01 = null; continue; }
    let lo = Infinity;
    let hi = -Infinity;
    for (let j = i - WIN; j <= i + WIN; j += 1) {
      const x = hours[j]?.tideM;
      if (x == null) continue;
      if (x < lo) lo = x;
      if (x > hi) hi = x;
    }
    hours[i].tideStage01 = hi > lo ? Math.max(0, Math.min(1, (v - lo) / (hi - lo))) : 0.5;
  }
  return hours;
}

// Magnitude fields — linear interpolate across short holes.
const DENSIFY_MAG = [
  'waveH', 'waveP', 'swellH', 'swellP', 'swellPeak',
  'swell2H', 'swell2P', 'swell2Peak',
  'swell3H', 'swell3P', 'swell3Peak',
  'windWaveH', 'windSpd', 'windGust', 'tideM',
];
// Direction / categorical — nearest neighbor (don't lerp bearings blindly here).
const DENSIFY_NEAR = [
  'waveDir', 'swellDir', 'swell2Dir', 'swell3Dir',
  'windDir', 'windWaveDir', 'tideState',
];

function densifyNeighbors(sortedTs, byT, t, key, maxGap, { numeric = true } = {}) {
  let prev = null;
  let next = null;
  for (let i = 0; i < sortedTs.length; i += 1) {
    const tt = sortedTs[i];
    const v = byT.get(tt)?.[key];
    const ok = numeric ? Number.isFinite(v) : v != null;
    if (!ok) continue;
    if (tt <= t) prev = tt;
    if (tt >= t && next == null) next = tt;
  }
  if (prev == null && next == null) return null;
  if (prev == null) return next - t <= maxGap ? byT.get(next)[key] : null;
  if (next == null) return t - prev <= maxGap ? byT.get(prev)[key] : null;
  if (next - prev > maxGap) return null;
  if (prev === t) return byT.get(prev)[key];
  if (next === t) return byT.get(next)[key];
  const a = byT.get(prev)[key];
  const b = byT.get(next)[key];
  if (!numeric || !Number.isFinite(a) || !Number.isFinite(b)) {
    return (t - prev <= next - t) ? a : b;
  }
  const f = (t - prev) / (next - prev || 1);
  return a + (b - a) * f;
}

/**
 * Fill missing hourly slots and null fields in a forecast series so boards
 * don't show afternoon holes when a provider skipped hours. Interpolates
 * magnitudes across gaps ≤ maxGapH hours; directions take the nearer sample.
 */
export function densifyForecastHours(hours, { maxGapH = 30 } = {}) {
  if (!Array.isArray(hours) || !hours.length) return hours || [];
  const byT = new Map();
  for (const h of hours) {
    if (!Number.isFinite(h?.t)) continue;
    const t = Math.round(h.t / HOUR) * HOUR;
    const prev = byT.get(t);
    // Prefer the sample with more swell signal when duplicates collide.
    if (!prev || ((h.swellH || 0) + (h.waveH || 0) > (prev.swellH || 0) + (prev.waveH || 0))) {
      byT.set(t, { ...h, t });
    }
  }
  const sortedTs = [...byT.keys()].sort((a, b) => a - b);
  if (sortedTs.length < 2) return sortedTs.map((t) => byT.get(t));

  const maxGap = Math.max(1, maxGapH) * HOUR;
  const t0 = sortedTs[0];
  const t1 = sortedTs[sortedTs.length - 1];
  const out = [];

  for (let t = t0; t <= t1; t += HOUR) {
    const base = byT.has(t) ? { ...byT.get(t) } : { t };
    let filled = false;
    for (const k of DENSIFY_MAG) {
      if (Number.isFinite(base[k])) continue;
      const v = densifyNeighbors(sortedTs, byT, t, k, maxGap, { numeric: true });
      if (Number.isFinite(v)) {
        base[k] = Math.round(v * 10) / 10;
        filled = true;
      }
    }
    for (const k of DENSIFY_NEAR) {
      if (base[k] != null) continue;
      const v = densifyNeighbors(sortedTs, byT, t, k, maxGap, { numeric: false });
      if (v != null) {
        base[k] = Number.isFinite(v) ? Math.round(v) : v;
        filled = true;
      }
    }
    // Skip inventing a blank hour when nothing could be filled and it wasn't observed.
    if (!byT.has(t) && !filled && base.swellH == null && base.waveH == null && base.windSpd == null) {
      continue;
    }
    if (!byT.has(t)) base._interp = true;
    out.push(base);
  }

  for (let i = 0; i < out.length; i += 1) {
    if (out[i].tideM == null) continue;
    const prev = out[i - 1]?.tideM;
    const next = out[i + 1]?.tideM;
    const ref = next != null ? next : out[i].tideM;
    const base = prev != null ? prev : out[i].tideM;
    const delta = ref - base;
    if (out[i].tideState == null) {
      out[i].tideState = Math.abs(delta) < 0.03 ? 'slack' : delta > 0 ? 'rising' : 'falling';
    }
  }
  attachTideStage(out);
  return out;
}

/** Normalize a conditions doc: densify hours + tide stage. */
export function normalizeForecastCond(cond) {
  if (!cond?.hours?.length) return cond;
  const hours = densifyForecastHours(cond.hours);
  return { ...cond, hours, rangeStart: hours[0]?.t ?? cond.rangeStart, rangeEnd: hours[hours.length - 1]?.t ?? cond.rangeEnd };
}

function buildHours(marine, wind, marineLong = {}) {
  const mt = marine.time || [];
  const wt = wind.time || [];
  const lt = marineLong.time || [];
  const grid = new Set();
  mt.forEach((t) => grid.add(Math.round(t / HOUR) * HOUR));
  wt.forEach((t) => grid.add(Math.round(t / HOUR) * HOUR));
  lt.forEach((t) => grid.add(Math.round(t / HOUR) * HOUR));
  const times = Array.from(grid).sort((a, b) => a - b);
  const mIdx = new Map(mt.map((t, i) => [Math.round(t / HOUR) * HOUR, i]));
  const wIdx = new Map(wt.map((t, i) => [Math.round(t / HOUR) * HOUR, i]));
  const lIdx = new Map(lt.map((t, i) => [Math.round(t / HOUR) * HOUR, i]));
  const at = (arr, i) => (arr && i != null && i >= 0 ? arr[i] : undefined);

  const hours = times.map((t) => {
    const mi = mIdx.has(t) ? mIdx.get(t) : null;
    const wi = wIdx.has(t) ? wIdx.get(t) : null;
    const li = lIdx.has(t) ? lIdx.get(t) : null;
    // The nearshore default model (Météo-France WAM) only forecasts swell ~10
    // days out; fall back to NCEP GFS Wave (16-day) for the extended tail.
    const pick = (key) => at(marine[key], mi) ?? at(marineLong[key], li);
    return {
      t,
      waveH: r1(pick('wave_height')),
      waveDir: r0(pick('wave_direction')),
      waveP: r1(pick('wave_period')),
      swellH: r1(pick('swell_wave_height')),
      swellDir: r0(pick('swell_wave_direction')),
      swellP: r1(pick('swell_wave_period')),
      // Convert the nearshore model's mean period to a surf-convention peak (Tp≈1.3·Tm).
      // For the extended tail (GFS-Wave only), its period is already near-peak — use as-is.
      swellPeak: Number.isFinite(at(marine.swell_wave_period, mi))
        ? peakFromMean(at(marine.swell_wave_period, mi))
        : r1(at(marineLong.swell_wave_period, li)),
      swell2H: r1(pick('secondary_swell_wave_height')),
      swell2Dir: r0(pick('secondary_swell_wave_direction')),
      swell2P: r1(pick('secondary_swell_wave_period')),
      swell2Peak: Number.isFinite(at(marine.secondary_swell_wave_period, mi))
        ? peakFromMean(at(marine.secondary_swell_wave_period, mi))
        : r1(at(marineLong.secondary_swell_wave_period, li)),
      swell3H: r1(pick('tertiary_swell_wave_height')),
      swell3Dir: r0(pick('tertiary_swell_wave_direction')),
      swell3P: r1(pick('tertiary_swell_wave_period')),
      swell3Peak: Number.isFinite(at(marine.tertiary_swell_wave_period, mi))
        ? peakFromMean(at(marine.tertiary_swell_wave_period, mi))
        : r1(at(marineLong.tertiary_swell_wave_period, li)),
      windWaveH: r1(pick('wind_wave_height')),
      windSpd: r1(at(wind.wind_speed_10m, wi)),
      windDir: r0(at(wind.wind_direction_10m, wi)),
      windGust: r1(at(wind.wind_gusts_10m, wi)),
      tideM: r1(at(marine.sea_level_height_msl, mi)),
    };
  });

  for (let i = 0; i < hours.length; i += 1) {
    if (hours[i].tideM == null) continue;
    const prev = hours[i - 1]?.tideM;
    const next = hours[i + 1]?.tideM;
    const ref = next != null ? next : hours[i].tideM;
    const base = prev != null ? prev : hours[i].tideM;
    const delta = ref - base;
    hours[i].tideState = Math.abs(delta) < 0.03 ? 'slack' : delta > 0 ? 'rising' : 'falling';
  }

  attachTideStage(hours);
  return hours;
}

// Fetch a spot's live forecast and return a tripConditions-shaped object
// (plus daily sunrise/sunset for daylight shading on the meteogram).
export async function fetchSpotForecast(lat, lng, days = 7, beachFacing = null) {
  const fdays = Math.max(1, Math.min(16, days));
  const [marine, wind, marineLong] = await Promise.all([
    getJson(
      `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lng}` +
        `&hourly=${MARINE_VARS}&forecast_days=${fdays}&timezone=auto&timeformat=unixtime&cell_selection=sea`
    ),
    getJson(
      `https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lng}` +
        `&hourly=${WIND_VARS}&daily=sunrise,sunset&forecast_days=${fdays}&timezone=auto&timeformat=unixtime`
    ),
    // NCEP GFS Wave (0.25°) forecasts the full 16 days — used to fill the swell
    // tail past day ~10 where the higher-res nearshore default model stops.
    fdays > 10
      ? getJson(
          `https://marine-api.open-meteo.com/v1/marine?latitude=${lat}&longitude=${lng}` +
            `&hourly=${MARINE_LONG_VARS}&forecast_days=${fdays}&timezone=auto&timeformat=unixtime&cell_selection=sea&models=ncep_gfswave025`
        ).catch(() => ({}))
      : Promise.resolve({}),
  ]);

  const m = marine.hourly || {};
  const w = wind.hourly || {};
  let ml = marineLong.hourly || {};
  // If the GFS tail landed on a dead coastal cell, re-sample offshore along the
  // break's facing bearing so the 10–16 day tail isn't a flat zero line.
  if (fdays > 10 && isDeadWaveSeries(ml) && Number.isFinite(beachFacing)) {
    const [oLat, oLng] = offsetPoint(lat, lng, beachFacing, 50);
    const alt = await getJson(
      `https://marine-api.open-meteo.com/v1/marine?latitude=${oLat}&longitude=${oLng}` +
        `&hourly=${MARINE_LONG_VARS}&forecast_days=${fdays}&timezone=auto&timeformat=unixtime&cell_selection=sea&models=ncep_gfswave025`
    ).catch(() => ({}));
    const ml2 = alt.hourly || {};
    if (!isDeadWaveSeries(ml2)) ml = ml2;
  }
  const hours = densifyForecastHours(buildHours(m, w, ml));
  if (!hours.length) throw new Error('no forecast data for this location');

  const wd = wind.daily || {};
  const daily = {
    sunrise: (wd.sunrise || []).filter((x) => Number.isFinite(x)),
    sunset: (wd.sunset || []).filter((x) => Number.isFinite(x)),
  };

  return {
    status: 'ready',
    fetchedAt: Date.now(),
    lat,
    lng,
    timezone: marine.timezone || wind.timezone || 'GMT',
    utcOffsetSec: marine.utc_offset_seconds ?? wind.utc_offset_seconds ?? 0,
    rangeStart: hours[0].t,
    rangeEnd: hours[hours.length - 1].t,
    units: 'metric',
    source: { swellWind: 'open-meteo', tide: hours.some((h) => h.tideM != null) ? 'open-meteo' : 'none' },
    hours,
    daily,
    tideExtremes: deriveTideExtremes(hours),
  };
}

// Wind quality relative to the spot's beach orientation. `beachFacing` is the
// compass direction the beach looks out to sea (i.e. onshore wind comes FROM
// that bearing). Returns 'glass' | 'offshore' | 'cross' | 'onshore' | null.
export function windQuality(windFromDeg, beachFacingDeg, kn) {
  if (kn != null && kn < 3) return 'glass';
  if (windFromDeg == null || beachFacingDeg == null || !Number.isFinite(beachFacingDeg)) return null;
  const d = Math.abs((((windFromDeg - beachFacingDeg + 540) % 360) - 180)); // 0=onshore … 180=offshore
  if (d >= 135) return 'offshore';
  if (d >= 45) return 'cross';
  return 'onshore';
}

export const WIND_QUALITY_COLOR = {
  offshore: '#43d17f',
  cross: '#e6c34a',
  onshore: '#e0734a',
  glass: '#34d8c2',
};
export const WIND_QUALITY_LABEL = {
  offshore: 'Offshore',
  cross: 'Cross-shore',
  onshore: 'Onshore',
  glass: 'Glassy',
};

// Resolve an hour's two swell trains, ordered so `primary` is the DOMINANT one by
// wave power (H²·T). Open-Meteo's spectral partitioning sometimes labels a
// smaller-but-present swell as the "primary" partition and a larger one as
// "secondary"; ordering by energy makes the bigger, more powerful train read as
// primary everywhere (chart line, face calc, tooltips) so the labels never lie.
export function swellTrains(h) {
  const empty = { primary: { ft: null, period: null, dir: null }, secondary: null, tertiary: null };
  if (!h) return empty;
  // Each model partition carries a peak period (surf convention) where available,
  // falling back to its mean period. Trains are then ranked by energy (H²·T) so the
  // most powerful swell is always "primary" regardless of how the model ordered them.
  const a = { ft: mToFt(h.swellH), period: h.swellPeak != null ? h.swellPeak : h.swellP, dir: h.swellDir };
  const b = { ft: mToFt(h.swell2H), period: h.swell2Peak != null ? h.swell2Peak : h.swell2P, dir: h.swell2Dir };
  const c = { ft: mToFt(h.swell3H), period: h.swell3Peak != null ? h.swell3Peak : h.swell3P, dir: h.swell3Dir };
  const e = (t) => (t.ft == null ? -1 : t.ft * t.ft * (t.period || 8));
  const trains = [a, b, c].filter((t) => t.ft != null && t.ft > 0).sort((x, y) => e(y) - e(x));
  if (!trains.length) {
    return { primary: { ft: mToFt(h.waveH), period: h.waveP, dir: h.waveDir }, secondary: null, tertiary: null };
  }
  return { primary: trains[0], secondary: trains[1] || null, tertiary: trains[2] || null };
}

// The surf-relevant height for an hour (ft): dominant swell train, else sea.
export const hourSurfFt = (h) => swellTrains(h).primary.ft;
// Secondary (lesser) swell train height (ft), when present.
export const hourSurf2Ft = (h) => swellTrains(h).secondary?.ft ?? null;
// The reported period (s) of the dominant train.
export const hourPeriodS = (h) => swellTrains(h).primary.period ?? null;
// The direction (deg) the dominant train comes from.
export const hourSwellDir = (h) => swellTrains(h).primary.dir ?? null;
export const hourWindKn = (h) => kmhToKn(h.windSpd);

// --------------------------- Wave-height calibration ---------------------------
// Open-Meteo reports deep-water significant swell height (Hs). The wave height
// that actually breaks at a spot depends on the swell's energy AND the spot's
// bathymetry/refraction — a focusing break (Pascuales, Nazaré, Puerto…) can turn
// a modest long-period swell into a much bigger face. We model the breaking/face
// height as a power law in Hs and period T:
//
//   faceFt = k · Hs^swellExp · (T / periodRef)^periodExp
//
// The coefficient k is fit to a known "anchor" session (the face height a real
// swell produced), so the curve always passes through your observed point. The
// exponents shape the EXTRAPOLATION away from it:
//   - swellExp < 1 (default 0.8): bigger swells magnify proportionally less, so
//     a huge swell doesn't blow up linearly (Komar-style breaking ∝ Hs^0.8).
//   - periodExp (default 0.5): longer-period swell of equal height breaks bigger
//     (energy ∝ Hs²·T → height ∝ √T).
// Disabled, or with no anchor and no legacy gain, the raw Hs is returned so
// uncalibrated spots behave exactly as before.
export const DEFAULT_CALIB = {
  enabled: false,
  gain: 1, // legacy linear multiplier (pre-anchor calibrations)
  swellExp: 0.8,
  periodExp: 0.5,
  periodRef: 10,
  anchor: null, // { faceFt, swellFt, periodS }
};

function calibParts(calib) {
  const c = { ...DEFAULT_CALIB, ...(calib || {}) };
  const ref = c.periodRef || 10;
  const hasAnchor = c.anchor && c.anchor.faceFt > 0 && c.anchor.swellFt > 0;
  // Legacy calibrations (a bare `gain`, no anchor) stay linear so they don't
  // silently change; anchored calibrations use the sublinear power law.
  const swellExp = hasAnchor ? (c.swellExp ?? 0.8) : 1;
  const periodExp = c.periodExp ?? 0.5;
  let k;
  if (hasAnchor) {
    const aT = c.anchor.periodS > 0 ? c.anchor.periodS : ref;
    const denom = Math.pow(c.anchor.swellFt, swellExp) * Math.pow(aT / ref, periodExp);
    k = denom > 0 ? c.anchor.faceFt / denom : c.gain || 1;
  } else {
    k = c.gain || 1;
  }
  return { enabled: c.enabled === true, ref, swellExp, periodExp, k };
}

export function calibrateFt(swellFt, periodS, calib) {
  if (swellFt == null) return null;
  const { enabled, ref, swellExp, periodExp, k } = calibParts(calib);
  if (!enabled) return swellFt;
  const T = periodS && periodS > 0 ? periodS : ref;
  return k * Math.pow(swellFt, swellExp) * Math.pow(T / ref, periodExp);
}

// Calibrated breaking/face height (ft) for an hour's dominant / lesser trains.
export const faceFt = (h, calib) => {
  const t = swellTrains(h);
  return calibrateFt(t.primary.ft, t.primary.period, calib);
};
export const face2Ft = (h, calib) => {
  const t = swellTrains(h);
  return t.secondary ? calibrateFt(t.secondary.ft, t.secondary.period, calib) : null;
};

const angleDiff = (a, b) => (a == null || b == null ? 0 : Math.abs((((a - b) % 360) + 540) % 360 - 180));

// --------------------------- Combined (combo) surf ---------------------------
// The surf you actually get is driven by ALL swell trains present, not just the
// dominant one. Wave energy is additive, so significant heights combine in
// quadrature: Hs = √(ΣHᵢ²) (BoM / Surfline convention). But two trains only
// stack into the SAME bigger face when they arrive from a similar direction; a
// crossed secondary makes its own separate peaks instead. So we weight the
// secondary train by directional alignment to the primary (cosΔ, floored at 0),
// combine energy, take an energy-weighted period, and run the spot calibration
// on that effective swell. Returns { ft (face), hs (effective swell), period, dir }.
export function combinedSurf(h, calib) {
  const { primary, secondary, tertiary } = swellTrains(h);
  if (primary.ft == null) return { ft: null, hs: null, period: null, dir: null };
  // Combine every train present: energy adds in quadrature (Hs=√ΣHᵢ²), but a train
  // only stacks into the SAME face when it arrives from a similar direction, so we
  // weight each non-primary train by directional alignment (cosΔ, floored at 0).
  // Period is the energy-weighted (blended) peak period across the stacked trains.
  let eSum = primary.ft * primary.ft;
  let pSum = primary.period * eSum;
  for (const t of [secondary, tertiary]) {
    if (!t || t.ft == null) continue;
    const align = Math.max(0, Math.cos((angleDiff(primary.dir, t.dir) * Math.PI) / 180));
    const ei = (t.ft * align) * (t.ft * align);
    if (ei <= 0) continue;
    eSum += ei;
    pSum += (t.period || primary.period) * ei;
  }
  const hs = Math.sqrt(eSum);
  const period = pSum / (eSum || 1);
  return { ft: calibrateFt(hs, period, calib), hs, period, dir: primary.dir };
}

// The headline forecast surf (calibrated face height) for an hour, accounting for
// combo swells. This is what should drive displays, alerts and strike scoring.
export const surfFaceFt = (h, calib) => combinedSurf(h, calib).ft;

// --------------------------- Wave consistency ---------------------------
// A set-frequency index (0-100), modeled after Surfline's "Wave Consistency":
// how often rideable set waves come, NOT quality. Per their findings, short-
// period windswell is MORE consistent (waves arrive often) while long-period
// groundswell is less consistent (defined sets with long lulls). Drivers:
//   - mean (energy-weighted) period: shorter ⇒ more frequent waves
//   - spectral richness: more energy in the secondary swell + wind-sea (a
//     broader spectrum) ⇒ more frequent, overlapping waves
// Independent of wind/quality, which we report separately.
export function waveConsistency(h) {
  if (!h) return null;
  const { primary, secondary, tertiary } = swellTrains(h);
  if (primary.ft == null) return null;
  const w1 = primary.ft * primary.ft;
  const w2 = secondary?.ft != null ? secondary.ft * secondary.ft : 0;
  const w3 = tertiary?.ft != null ? tertiary.ft * tertiary.ft : 0;
  const hw = mToFt(h.windWaveH);
  const ww = hw != null ? hw * hw : 0;
  const wTot = w1 + w2 + w3 + ww || 1;
  const tMean = (primary.period * w1 + (secondary?.period || primary.period) * w2
    + (tertiary?.period || primary.period) * w3 + 6 * ww) / wTot;
  const richness = (w2 + w3 + ww) / wTot; // fraction of energy outside the primary train
  const freqScore = Math.max(0, Math.min(1, (19 - tMean) / (19 - 5)));
  const c = 0.12 + 0.6 * freqScore + 0.28 * richness;
  return Math.round(Math.max(0, Math.min(1, c)) * 100);
}
export const consistencyLabel = (c) => (c == null ? '' : c >= 70 ? 'Very consistent' : c >= 45 ? 'Consistent' : c >= 25 ? 'Patchy' : 'Lully');
export function consistencyColor(c) {
  if (c == null) return 'var(--muted)';
  if (c >= 70) return '#43d17f';
  if (c >= 45) return '#7fce4a';
  if (c >= 25) return '#e6c34a';
  return '#e0734a';
}

export const DEFAULT_THRESHOLDS = {
  enabled: true,
  minSwellFt: 3,
  minPeriodS: 10,
  maxWindKn: 12,
};

// --------------------------- Per-spot tide preference ---------------------------
// Some breaks only work on a given tide. `mode` is a preset band over the
// normalized tide stage (0 = low, 1 = high); `custom` uses explicit min/max
// stage (0..1). 'any' (the default) imposes no tide constraint.
export const DEFAULT_TIDE = { mode: 'any', minStage: null, maxStage: null };
export const TIDE_MODE_LABEL = { any: 'Any tide', low: 'Low', mid: 'Mid', high: 'High', custom: 'Custom' };

export function tideBand(tide) {
  const t = { ...DEFAULT_TIDE, ...(tide || {}) };
  if (t.mode === 'custom' && t.minStage != null && t.maxStage != null) {
    const lo = Math.max(0, Math.min(1, t.minStage));
    const hi = Math.max(0, Math.min(1, t.maxStage));
    return hi > lo ? { lo, hi } : null;
  }
  if (t.mode === 'low') return { lo: 0, hi: 0.4 };
  if (t.mode === 'mid') return { lo: 0.3, hi: 0.7 };
  if (t.mode === 'high') return { lo: 0.6, hi: 1 };
  return null; // 'any' or unrecognized => no constraint
}

// Does this hour's tide stage fall within the spot's preferred band? Spots with
// no tide data (extended-range hours) are never disqualified on tide.
export function tideOk(h, tide) {
  const band = tideBand(tide);
  if (!band) return true;
  const s = h?.tideStage01;
  if (s == null) return true;
  return s >= band.lo && s <= band.hi;
}

export const tideStageLabel = (s) => (s == null ? '—' : s >= 0.66 ? 'High' : s >= 0.34 ? 'Mid' : 'Low');

// Does an hour clear the spot's "good surf" bar? `ft` is the (optionally
// calibrated) breaking height, so thresholds read in the spot's own face heights.
function hourIsGood(h, th, calib, tide) {
  const ft = surfFaceFt(h, calib);
  if (ft == null) return false;
  if (th.minSwellFt != null && ft < th.minSwellFt) return false;
  if (th.minPeriodS != null) {
    const p = hourPeriodS(h);
    if (p == null || p < th.minPeriodS) return false;
  }
  if (th.maxWindKn != null) {
    const kn = hourWindKn(h);
    if (kn != null && kn > th.maxWindKn) return false;
  }
  if (!tideOk(h, tide)) return false;
  return true;
}

// Evaluate a forecast against thresholds: returns the current sample, whether an
// alert is firing, and the matching forecast windows (consecutive good hours,
// tolerating a single-hour gap) from now forward.
export function evaluateAlerts(cond, thresholds, calib = null, tide = null, nowMs = Date.now()) {
  const th = { ...DEFAULT_THRESHOLDS, ...(thresholds || {}) };
  const hours = cond?.hours || [];
  const nowSec = nowMs / 1000;
  const future = hours.filter((h) => h.t >= nowSec - HOUR);

  const windows = [];
  let cur = null;
  let lastGoodT = null;
  for (const h of future) {
    if (hourIsGood(h, th, calib, tide)) {
      const ft = surfFaceFt(h, calib);
      const p = hourPeriodS(h);
      if (cur && lastGoodT != null && h.t - lastGoodT <= 2 * HOUR) {
        cur.endSec = h.t;
        cur.peakFt = Math.max(cur.peakFt, ft || 0);
        cur.peakPeriod = Math.max(cur.peakPeriod, p || 0);
        cur.minFt = Math.min(cur.minFt, ft || cur.minFt);
      } else {
        cur = { startSec: h.t, endSec: h.t, peakFt: ft || 0, minFt: ft || 0, peakPeriod: p || 0 };
        windows.push(cur);
      }
      lastGoodT = h.t;
    } else {
      cur = null;
    }
  }

  return {
    enabled: th.enabled !== false,
    thresholds: th,
    alerting: th.enabled !== false && windows.length > 0,
    windows,
    nextWindow: windows[0] || null,
  };
}

// ---------------------------- Strike missions ----------------------------
// "Strike" thresholds describe what *epic* looks like for a spot (a higher bar
// than the everyday alert thresholds) plus the wind the spot can tolerate. The
// tolerable wind is a max sustained speed AND a wind-direction arc (FROM
// bearings, swept clockwise) — wind outside that arc, or stronger than the cap,
// disqualifies the window.
export const DEFAULT_STRIKE = {
  enabled: true,
  minSwellFt: 6,
  minPeriodS: 12,
  maxWindKn: 16,
  windDirFrom: null, // null => derive the offshore band from beachFacing
  windDirTo: null,
  // Swell-direction window (FROM bearings, swept clockwise). Reef passes and
  // point setups only light up in a band of swell angles — outside it the
  // swell marches past the spot. null/null => any direction works.
  swellDirFrom: null,
  swellDirTo: null,
};

// Size-scaled strike defaults for a spot, derived from its calibration anchor so
// "epic" means something different at Malibu (a 5ft face) than at Pascuales or
// Nazaré (20-40ft faces). The epic bar is ~0.7× the anchored session's face
// (a notable swell), and the min period scales with the anchor's period. Only
// applies when calibration is active (faces are in spot-scaled feet); otherwise
// the flat DEFAULT_STRIKE (raw-swell feet) is used. User overrides still win.
export function spotStrikeDefaults(calib) {
  const c = calib && calib.enabled && calib.anchor && calib.anchor.faceFt > 0 ? calib : null;
  if (!c) return { ...DEFAULT_STRIKE };
  const epic = Math.max(4, Math.round(c.anchor.faceFt * 0.7));
  const per = Math.max(8, Math.min(15, Math.round((c.anchor.periodS || 12) * 0.78)));
  return { ...DEFAULT_STRIKE, minSwellFt: epic, minPeriodS: per };
}

const norm360 = (a) => ((a % 360) + 360) % 360;

// Resolve the tolerable wind-direction arc {from,to} (FROM bearings, clockwise).
// Explicit values win; otherwise derive the offshore band from beachFacing
// (offshore wind blows FROM the opposite of the way the beach faces). Returns
// null when there's no basis for a direction constraint (any direction is OK).
export function resolveWindArc(strike, beachFacing) {
  const s = strike || {};
  const from = Number(s.windDirFrom);
  const to = Number(s.windDirTo);
  if (Number.isFinite(from) && Number.isFinite(to)) return { from: norm360(from), to: norm360(to) };
  if (Number.isFinite(Number(beachFacing))) {
    const offshore = norm360(Number(beachFacing) + 180);
    return { from: norm360(offshore - 60), to: norm360(offshore + 60) };
  }
  return null;
}

function arcContains(deg, arc) {
  if (!arc) return true; // no constraint
  const span = norm360(arc.to - arc.from);
  if (span === 0) return true; // degenerate arc => treat as unconstrained
  return norm360(deg - arc.from) <= span;
}

// Resolve the spot's preferred swell-direction arc (FROM bearings, clockwise),
// or null when unconstrained.
export function resolveSwellArc(strike) {
  const s = strike || {};
  const from = Number(s.swellDirFrom);
  const to = Number(s.swellDirTo);
  if (Number.isFinite(from) && Number.isFinite(to)) return { from: norm360(from), to: norm360(to) };
  return null;
}

// Does this hour's dominant swell come from within the spot's preferred window?
// Unknown direction never disqualifies (extended-tail hours can lack it).
export function swellDirOk(h, strike) {
  const arc = resolveSwellArc(strike);
  if (!arc) return true;
  const dir = hourSwellDir(h);
  if (dir == null) return true;
  return arcContains(dir, arc);
}

// Is the wind tolerable for a strike this hour?
export function windTolerable(h, strike, beachFacing) {
  const kn = hourWindKn(h);
  if (kn != null && strike.maxWindKn != null && kn > strike.maxWindKn) return false;
  if (kn != null && kn < 3) return true; // glassy: always fine
  if (h.windDir == null) return true; // unknown direction: don't disqualify on it
  return arcContains(h.windDir, resolveWindArc(strike, beachFacing));
}

function hourEpic(h, strike, beachFacing, calib, tide) {
  const ft = surfFaceFt(h, calib);
  if (ft == null) return false;
  if (strike.minSwellFt != null && ft < strike.minSwellFt) return false;
  if (strike.minPeriodS != null) {
    const p = hourPeriodS(h);
    if (p == null || p < strike.minPeriodS) return false;
  }
  if (!swellDirOk(h, strike)) return false;
  if (!tideOk(h, tide)) return false;
  return windTolerable(h, strike, beachFacing);
}

// Wave energy/power proxy (∝ H²·T) using the calibrated face height, per hour —
// used to score windows so the ranking reflects each spot's real size.
const hourEnergy = (h, calib) => {
  const ft = surfFaceFt(h, calib);
  if (ft == null) return 0;
  return ft * ft * (hourPeriodS(h) || 8);
};

// Per-day peak fold for a strike window: buckets epic hours by the VIEWER's
// local day (matching the strike calendar's axis, which draws local-midnight
// columns) and tracks each day's own peak — so a 4-day swell renders as four
// day segments sized/colored by that day's height, not one flat bar at the
// window-wide peak.
const localDaySec = (t) => new Date(t * 1000).setHours(0, 0, 0, 0) / 1000;
function foldWindowDay(win, h, ft, cs, p, cons) {
  const daySec = localDaySec(h.t);
  let day = win.days[win.days.length - 1];
  if (!day || day.daySec !== daySec) {
    win.days.push({
      daySec, startSec: h.t, endSec: h.t,
      peakFt: ft, peakRawFt: cs.hs, peakPeriod: p, peakDir: hourSwellDir(h) ?? null,
      peakCons: cons, peakHour: h,
      peakWindDir: h.windDir ?? null, peakWindKn: hourWindKn(h),
    });
    return;
  }
  day.endSec = h.t;
  if (ft > day.peakFt) {
    day.peakFt = ft;
    day.peakRawFt = cs.hs;
    day.peakPeriod = p;
    day.peakDir = hourSwellDir(h) ?? day.peakDir;
    day.peakCons = cons;
    day.peakHour = h;
    day.peakWindDir = h.windDir ?? day.peakWindDir;
    day.peakWindKn = hourWindKn(h) ?? day.peakWindKn;
  }
}

// Evaluate a forecast for strike windows: consecutive epic hours (tolerating a
// 2h gap), each scored by INTEGRATED energy across the window so sustained,
// multi-day swells outrank a brief peak. Returns windows sorted best-first.
export function evaluateStrike(cond, strike, beachFacing, calib = null, tide = null, nowMs = Date.now()) {
  const s = { ...DEFAULT_STRIKE, ...(strike || {}) };
  const hours = cond?.hours || [];
  const nowSec = nowMs / 1000;
  const future = hours.filter((h) => h.t >= nowSec - HOUR);

  const windows = [];
  let cur = null;
  let lastT = null;
  for (const h of future) {
    if (hourEpic(h, s, beachFacing, calib, tide)) {
      const cs = combinedSurf(h, calib);
      const ft = cs.ft || 0;
      const p = hourPeriodS(h) || 0;
      const e = hourEnergy(h, calib);
      const cons = waveConsistency(h);
      if (cur && lastT != null && h.t - lastT <= 2 * HOUR) {
        cur.endSec = h.t;
        cur.energy += e;
        cur.nHours += 1;
        cur.sumFt += ft;
        cur.consSum += cons || 0;
        if (ft > cur.peakFt) {
          cur.peakFt = ft;
          cur.peakRawFt = cs.hs;
          cur.peakPeriod = p;
          cur.peakSec = h.t;
          cur.peakCons = cons;
          cur.peakDir = hourSwellDir(h) ?? cur.peakDir;
          cur.peakHour = h;
          cur.peakWindDir = h.windDir ?? cur.peakWindDir;
          cur.peakWindKn = hourWindKn(h) ?? cur.peakWindKn;
        }
      } else {
        cur = {
          startSec: h.t, endSec: h.t, peakSec: h.t,
          peakFt: ft, peakRawFt: cs.hs, peakPeriod: p, peakDir: hourSwellDir(h) ?? null,
          peakCons: cons, consSum: cons || 0, peakHour: h,
          peakWindDir: h.windDir ?? null, peakWindKn: hourWindKn(h),
          energy: e, nHours: 1, sumFt: ft,
          days: [],
        };
        windows.push(cur);
      }
      foldWindowDay(cur, h, ft, cs, p, cons);
      lastT = h.t;
    } else {
      cur = null;
    }
  }

  windows.forEach((wn) => {
    wn.durationH = Math.round((wn.endSec - wn.startSec) / HOUR) + 1;
    wn.meanFt = wn.sumFt / Math.max(1, wn.nHours);
    wn.meanCons = Math.round(wn.consSum / Math.max(1, wn.nHours));
    wn.score = wn.energy; // integrated H²·T already rewards sustained windows
  });
  windows.sort((a, b) => b.score - a.score);
  return { enabled: s.enabled !== false, strike: s, windows, best: windows[0] || null };
}
